import type { BriefingLastSession, BriefingPR } from '../../api/types'
import { Panel, TOKENS } from '../ui/dashboard-primitives'
import { formatDuration } from '../../lib/format'
import { toLb } from '../../lib/units'
import { useUnits } from '../../lib/UnitsProvider'
import { CountUp } from './CountUp'

function fmtDate(iso: string): string {
  return new Date(`${iso}T00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function Chip({ children, amber }: { children: React.ReactNode; amber?: boolean }) {
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.5875rem] tracking-[0.16em] uppercase border whitespace-nowrap"
      style={{
        fontFamily: 'var(--font-mono)',
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        background: amber ? 'rgba(232,197,160,0.12)' : TOKENS.creamHairline,
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        borderColor: amber ? 'rgba(232,197,160,0.28)' : TOKENS.creamBorder,
        color: amber ? TOKENS.amber : TOKENS.creamDim,
      }}
    >
      {children}
    </span>
  )
}

export function SessionDigest({
  session,
  prs,
  headline,
}: {
  session: BriefingLastSession
  prs: BriefingPR[]
  headline: string | null
}) {
  const { formatLoad, load } = useUnits()
  const prNames = new Set(prs.map((p) => p.exercise_name))
  // CountUp animates a raw number, so it can't take a formatted string --
  // convert the underlying kg value via useUnits().load (never a guessed
  // unit), same pattern as RangeCounters.tsx's own tonnage counter.
  const totalLoadKg = session.total_load_kg ?? 0
  const totalLoadDisplay = load === 'lb' ? toLb(totalLoadKg) : totalLoadKg

  return (
    <Panel variant="light" className="px-7 py-7 sm:px-7.5">
      <div className="flex items-baseline gap-3.5 flex-wrap mb-1">
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: '1.5rem',
            letterSpacing: '-0.02em',
            color: TOKENS.cream,
          }}
        >
          {headline || 'Last session'}
        </h3>
        <Chip>{fmtDate(session.date)}</Chip>
        {prs.length > 0 && <Chip amber>{prs.length} PR</Chip>}
      </div>

      <div className="flex items-baseline gap-2.5 mt-4 mb-5">
        <span
          className="tabular-nums leading-none"
          style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 'clamp(2.25rem, 4vw, 3rem)', color: TOKENS.cream }}
        >
          <CountUp value={totalLoadDisplay} comma />
        </span>
        <span
          className="text-[0.8125rem] tracking-[0.1em]"
          style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
        >
          {load} moved · {session.exercises.length} exercises
        </span>
      </div>

      <div className="grid">
        {session.exercises.map((ex, i) => {
          const isPR = prNames.has(ex.exercise_name)
          // A hold is logged: it has a duration, weight 0 and no reps. The old
          // test (`top_weight_kg != null && top_reps != null`) called that
          // "not logged" on the card summarising the session the user had just
          // finished -- the third screen with this same defect.
          const timed = ex.top_duration_sec != null && ex.top_duration_sec > 0
          const logged = timed || (ex.top_weight_kg != null && ex.top_reps != null)
          return (
            <div
              key={`${ex.exercise_name}-${i}`}
              className="grid items-baseline gap-x-[18px] py-2.5"
              style={{
                gridTemplateColumns: '1fr auto auto',
                borderTop: i === 0 ? undefined : '1px solid ' + TOKENS.creamHairline,
              }}
            >
              <span
                className="text-[0.875rem] truncate"
                style={{ color: isPR ? TOKENS.cream : logged ? TOKENS.creamSoft : TOKENS.creamFaint }}
              >
                {ex.exercise_name}
              </span>
              <span
                className="text-[0.75rem] tracking-[0.08em]"
                style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
              >
                {timed ? 'hold' : logged ? `${ex.top_reps} reps` : 'no reps'}
              </span>
              <span
                className="text-[0.875rem] tabular-nums text-right min-w-[5.5rem]"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: isPR ? TOKENS.amber : logged ? TOKENS.cream : TOKENS.creamFaint,
                  textDecoration: logged ? undefined : 'line-through',
                  // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                  textDecorationColor: 'rgba(245,237,225,0.25)',
                }}
              >
                {/* C-D5: weight_kg on a bodyweight exercise means ADDED
                    load, so a stored 0 means nothing was added and '0 kg'
                    reads as no resistance. A hold leads with its duration,
                    which is the thing that was achieved. */}
                {timed
                  ? formatDuration(ex.top_duration_sec)
                  : ex.top_weight_kg != null && ex.top_weight_kg > 0
                    ? formatLoad(ex.top_weight_kg)
                    : '·'}
              </span>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}
